import { ENTITY_ICONS } from "@/services/entityIcons"
import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"

/**
 * @fileOverview The onboarding walkthrough's catalog — the single source of truth for what a new user
 * is shown. Pure data: no Vue imports, no DOM access. See docs/onboarding-tour.md.
 *
 * The walkthrough is **one linear sequence** of steps, each playing a short screen recording of the
 * real app. Steps are grouped into {@link ONBOARDING_CHAPTERS} — a chapter is one *feature*, which is
 * what makes a whole feature droppable: a tenant that has not bought personnel planning never sees
 * that chapter at all. Chapters also name the step on screen and segment the progress bar.
 *
 * Nothing here points at a DOM element. An earlier version spotlit the real UI and broke on every
 * viewport where the navigation drawer is off-canvas; recordings are immune to that by construction,
 * and they can show a filled-in report, which a brand-new empty tenant never can.
 */

export interface OnboardingChapter {
  /** Suffix under `onboarding.chapter.*` for the title, and the chapter's stable id. */
  key: string
  icon: string
}

export interface OnboardingStep {
  /** Suffix under `onboarding.step.*` for the title/text, and the step's stable id. */
  key: string
  /** Which {@link ONBOARDING_CHAPTERS} entry this belongs to. Steps of one chapter gate together. */
  chapter: string
  /**
   * Basename of the clip under `public/onboarding/` — `<video>.mp4` plus `<video>.jpg` as its poster.
   * Recorded by `marketing/capture-onboarding.mjs`; the ids there and here must match.
   */
  video: string
  /** Shown only if the tenant is entitled to at least one of these feature-sets. Absent = always. */
  featureSets?: string[]
  /** Shown only if the user holds at least one of these permissions. Absent = always. */
  permissions?: { value: string }[]
  /**
   * A drawer entry's gate id whose permission check this step reuses — see `isNavEntryEnabled`.
   * Keeps the permission lists in `services/navigation.js` alone; duplicating them here would be a
   * guaranteed drift bug the moment one of them changes.
   */
  navEntry?: string
}

/** Both sides of "may read this kind of report". The two report chapters gate independently. */
const VIEW_HOURLYWAGEREPORT = [
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
]

const VIEW_CONSTRUCTIONREPORT = [
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
]

/**
 * The feature groups, in the order the walkthrough visits them — which is the order the work happens
 * in on a real site: set up your data, open a project, record what was done, plan who does it next,
 * watch the money, send the invoice.
 */
export const ONBOARDING_CHAPTERS: OnboardingChapter[] = [
  { key: "basics", icon: "mdi-database-outline" },
  { key: "projects", icon: "mdi-briefcase-outline" },
  // Two chapters, not one "Berichte": they are separate documents with separate permissions, and a
  // shared heading named neither of them for the user reading about one.
  { key: "hourlywagereport", icon: ENTITY_ICONS.hourlywagereport },
  { key: "constructionreport", icon: ENTITY_ICONS.constructionreport },
  { key: "planning", icon: "mdi-calendar-account" },
  { key: "costs", icon: "mdi-cash-clock" },
  { key: "invoicing", icon: "mdi-file-document-multiple" },
]

/**
 * The walkthrough itself. Kept deliberately short — seven steps, one per thing a new customer has to
 * understand before the product makes sense. Everything else is discoverable once those land.
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    key: "stammdaten",
    chapter: "basics",
    video: "stammdaten",
    featureSets: [FEATURE_SETS.CORE],
  },
  {
    key: "projekt",
    chapter: "projects",
    video: "projekt",
    featureSets: [FEATURE_SETS.PROJECT_MANAGEMENT],
    navEntry: "nav-project",
  },
  {
    key: "stundenlohnbericht",
    chapter: "hourlywagereport",
    video: "stundenlohnbericht",
    featureSets: [FEATURE_SETS.REPORTING],
    permissions: VIEW_HOURLYWAGEREPORT,
  },
  {
    key: "bautagesbericht",
    chapter: "constructionreport",
    video: "bautagesbericht",
    featureSets: [FEATURE_SETS.REPORTING],
    permissions: VIEW_CONSTRUCTIONREPORT,
  },
  {
    key: "personalplanung",
    chapter: "planning",
    video: "personalplanung",
    featureSets: [FEATURE_SETS.PERSONNEL_PLANNING],
    navEntry: "nav-personnelplanning",
  },
  {
    key: "nachkalkulation",
    chapter: "costs",
    video: "nachkalkulation",
    // Baseline, not an add-on: nothing gates the cost views, so `nav-costs` (a permission) is the
    // only thing that can hide this step. It rode on ADVANCED_ANALYTICS until that feature-set was
    // removed for advertising a gate that never existed.
    featureSets: [FEATURE_SETS.CORE],
    navEntry: "nav-costs",
  },
  {
    key: "abrechnung",
    chapter: "invoicing",
    video: "abrechnung",
    featureSets: [FEATURE_SETS.INVOICING],
    navEntry: "nav-invoice",
  },
]

/** Where the clips are served from. `public/` is copied verbatim, so this is a real URL prefix. */
export const ONBOARDING_VIDEO_BASE = "/onboarding"

/**
 * The closing screen's clip: the help menu being opened on "Rundgang starten".
 *
 * Not a step — it belongs to the outro, and it is framed differently from the rest (cropped to the
 * toolbar corner rather than full-frame), which is why it is named here instead of in the list above.
 */
export const ONBOARDING_OUTRO_VIDEO = "hilfe"

/**
 * Feature-sets that can still be bought, i.e. everything except the one every plan includes. Used for
 * the closing hint, which stays generic because some of these come with a higher tier and others are
 * sold as add-ons. The unreleased ones (not on sale yet) are left out where the hint is decided
 * (`useOnboardingTour`), since only /principal knows which they are.
 */
export const UPSELLABLE_FEATURE_SETS: string[] = Object.values(FEATURE_SETS).filter(fs => fs !== FEATURE_SETS.CORE)
